import { useEffect, useMemo, useRef, useState } from "react";
import cloneDeep from "lodash/cloneDeep";

import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

// API
import { getMyWashoutsSidePanelTree } from "@/Washouts/api/MyWashout/queries";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";

// Helpers
import { getDepartmentalPosition } from "@/Common/helpers/Positions";

// Components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import MyWashoutContainer from "./MyWashoutContainer";
import WashoutsSideBar from "@/Washouts/layouts/WashoutsSideBar";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

const QUERY_IDENTIFIERS = ["getWashout"];

const getTargetExpansion = (commissionCycle) => {
	if (!commissionCycle?.list?.length) {
		return [];
	}

	const payPeriods = commissionCycle.list.filter((cycle) => moment().isBetween(cycle.startDate, cycle.endDate));
	let period = commissionCycle.list.at(-1);

	if (payPeriods?.length) {
		period = payPeriods.at(-1);
	}

	const yearId = commissionCycle.year;
	const commissionCycleId = commissionCycle._id;
	const periodId = period._id;

	return [yearId, commissionCycleId, periodId];
};

const getCommissionCycleActive = (tree) => {
	let commissionCycles = [];

	for (const year of tree) {
		const activeOrClosingCycles = year.list
			.filter((cycle) => ["ACTIVE", "CLOSING"].includes(cycle.status))
			.map((item) => ({ ...item, year: year._id }));
		commissionCycles = [...commissionCycles, ...activeOrClosingCycles];
	}

	const commissionCycle = commissionCycles?.[0];
	if (!commissionCycle) {
		return [];
	}

	return getTargetExpansion(commissionCycle);
};

export default function MyWashoutLayer() {
	const { clearCache } = useCacheCleaner();
	const queryClient = useQueryClient();
	// Local references
	const lastSelected = useRef(null);
	const open = useRef(true);

	// Hooks
	const user = useSelector((state) => state.user.user);
	const location = useSelector((state) => state.location.location);
	const { addSubscription, clearSubscriptionContext, getSubscription } = useSubscriptions();

	// States
	const [selected, setSelected] = useState(null);
	const [positionToUse, setPositionToUse] = useState(null);
	const [positionIsDepartmental, setPositionIsDepartmental] = useState(null);

	// Washout subscriptions
	const { useWashoutSubscriptions } = useFactoryWashout();
	const subscribeWashout = useWashoutSubscriptions(positionToUse, positionIsDepartmental);
	const onCreateWashout = subscribeWashout?.get("onCreateWashout");
	const onUpdateWashout = subscribeWashout?.get("onUpdateWashout");

	const { data: sidePanelTree, isLoading } = useQuery({
		queryKey: ["myWashoutsTree", location?.locationId, user?._id],
		queryFn: async () => getMyWashoutsSidePanelTree(location?.locationId, user?._id),
		staleTime: Infinity,
		enabled: !!location?.locationId && !!user?._id,
	});

	const expanded = useMemo(() => {
		if (sidePanelTree?.length) {
			return getCommissionCycleActive(sidePanelTree);
		}
		return [];
	}, [sidePanelTree]);

	const handleSelect = (currentSelected) => {
		if (currentSelected._id !== selected?._id) {
			setPositionIsDepartmental(currentSelected.isDepartmental ? "Departmental" : "Producer");
			setPositionToUse(getDepartmentalPosition(currentSelected.position));
			setSelected(currentSelected);
		}
	};

	const myWashoutSubscription = getSubscription(["myWashout", selected?.commissionCycleId, selected?.payPeriodId, selected?.userId]);

	myWashoutSubscription?.subscriptions?.[onUpdateWashout?.getter]?.setCallback((result) => {
		const washoutUpdated = result?.data?.[onUpdateWashout?.getter];
		queryClient?.setQueriesData?.(
			{ queryKey: ["getWashout", selected?.payPeriodId, selected?.position, selected?.userId] },
			(oldData) => {
				const newData = cloneDeep(oldData);
				if (newData?._id === washoutUpdated?._id) {
					newData.summary = washoutUpdated.summary;
				}
				return newData;
			}
		);
	});

	myWashoutSubscription?.subscriptions?.[onCreateWashout?.getter]?.setCallback((result) => {
		const washoutUpdated = result?.data?.[onCreateWashout?.getter];
		queryClient?.setQueryData?.(["getWashout", selected?.payPeriodId, selected?.position, selected?.userId], (oldData) => {
			const newData = cloneDeep(oldData);
			if (newData?.employee?._id === washoutUpdated?.employee?._id) {
				return washoutUpdated;
			} // update wash
			return newData;
		});
	});

	useEffect(() => {
		if (selected && onCreateWashout?.query && onUpdateWashout?.query) {
			addSubscription(
				["myWashout", selected?.commissionCycleId, selected?.payPeriodId, selected?.userId],
				[
					{
						context: onCreateWashout?.getter,
						query: onCreateWashout?.query?.(),
						variables: {
							commissionCycleId: selected?.commissionCycleId,
							payPeriodId: selected?.payPeriodId,
							userId: selected?.userId,
						},
					},
					{
						context: onUpdateWashout?.getter,
						query: onUpdateWashout?.query?.(),
						variables: {
							commissionCycleId: selected?.commissionCycleId,
							payPeriodId: selected?.payPeriodId,
							userId: selected?.userId,
						},
					},
				]
			);
		}
	}, [selected]);

	useEffect(() => {
		if (!location) {
			return;
		}

		return () => {
			clearSubscriptionContext();
			clearCache(QUERY_IDENTIFIERS);
			setSelected(null);
			lastSelected.current = null;
			open.current = true;
		};
	}, [location]);

	return (
		<BodyLayer
			floatingContent={
				<CollapsibleLeftPanel
					loading={isLoading}
					permanentTrigger={open.current}
					open={true}
					control={
						<WashoutsSideBar
							treeDefinition={{ tree: sidePanelTree }}
							selected={selected?._id ?? ""}
							expanded={expanded}
							loading={isLoading}
							optionsDisabled={true}
							onSelectAction={handleSelect}
						/>
					}
				/>
			}
		>
			{selected && (
				<MyWashoutContainer
					selected={selected}
					location={location}
					user={{
						_id: user?._id,
						position: positionToUse,
					}}
				/>
			)}
		</BodyLayer>
	);
}
